import type { AppAction } from "@/app/state/actions";
import type { AppState, Chat } from "@/app/state/types";
import { upsertChatSummary } from "@/features/chats/lib/chatSummary";
import { setMapValue } from "@/app/state/reducerHelpers";
import {
	markAgentChatsRead,
	markWorkerRowsRead,
	upsertAgentUnreadCount,
} from "@/features/chats/lib/chatReadState";

function syncChatAgentBinding(
	source: Map<string, string>,
	chat: Partial<Chat> | null | undefined,
): Map<string, string> {
	const chatId = String(chat?.chatId || "").trim();
	const agentKey = String(chat?.agentKey || chat?.firstAgentKey || "").trim();
	if (!chatId || !agentKey || source.get(chatId) === agentKey) {
		return source;
	}
	const next = new Map(source);
	next.set(chatId, agentKey);
	return next;
}

function syncChatAgentBindings(
	source: Map<string, string>,
	chats: Array<Partial<Chat>>,
): Map<string, string> {
	return chats.reduce(syncChatAgentBinding, source);
}

function resolveChatAgentKey(chat: Partial<Chat> | null | undefined): string {
	return String(chat?.agentKey || chat?.firstAgentKey || "").trim();
}

function hasLastRunForTemporaryPinnedAgent(
	chats: Array<Partial<Chat>>,
	agentKey: string,
): boolean {
	const normalizedAgentKey = String(agentKey || "").trim();
	if (!normalizedAgentKey) {
		return false;
	}
	return chats.some((chat) => (
		resolveChatAgentKey(chat) === normalizedAgentKey
		&& Boolean(String(chat?.lastRunId || "").trim())
	));
}

function clearTemporaryPinForChats(
	state: AppState,
	chats: Array<Partial<Chat>>,
): string {
	return hasLastRunForTemporaryPinnedAgent(chats, state.temporaryPinnedAgentKey)
		? ""
		: state.temporaryPinnedAgentKey;
}

export function reduceNavigationState(
	state: AppState,
	action: AppAction,
): AppState | null {
	switch (action.type) {
		case "SET_AGENTS":
			return { ...state, agents: action.agents };
		case "SET_TEAMS":
			return { ...state, teams: action.teams };
		case "SET_CHATS":
			return {
				...state,
				chats: action.chats,
				temporaryPinnedAgentKey: clearTemporaryPinForChats(state, action.chats),
				chatAgentById: syncChatAgentBindings(
					state.chatAgentById,
					action.chats,
				),
			};
		case "SET_AUTOMATIONS":
			return { ...state, automations: action.automations };
		case "START_SIDEBAR_REQUEST":
			return {
				...state,
				sidebarPendingRequestCount: state.sidebarPendingRequestCount + 1,
			};
		case "FINISH_SIDEBAR_REQUEST":
			return {
				...state,
				sidebarPendingRequestCount: Math.max(
					0,
					state.sidebarPendingRequestCount - 1,
				),
			};
		case "UPSERT_CHAT": {
			const chats = upsertChatSummary(state.chats, action.chat);
			return {
				...state,
				chats,
				temporaryPinnedAgentKey: clearTemporaryPinForChats(state, chats),
				chatAgentById: syncChatAgentBinding(
					state.chatAgentById,
					action.chat,
				),
			};
		}
		case "CHAT_DELETED":
		case "CHAT_ARCHIVED": {
			const chatId = String(action.chatId || "");
			return {
				...state,
				chats: state.chats.filter((chat) => String(chat.chatId || "") !== chatId),
				workerRelatedChats: state.workerRelatedChats.filter(
					(chat) => String(chat.chatId || "") !== chatId,
				),
			};
		}
		case "CHAT_RENAMED": {
			const chatId = String(action.chatId || "").trim();
			const chatName = String(action.chatName || "").trim();
			if (!chatId || !chatName) {
				return state;
			}
			const renameChat = <T extends { chatId?: string; chatName?: string }>(
				chat: T,
			): T =>
				String(chat.chatId || "") === chatId ? { ...chat, chatName } : chat;
			return {
				...state,
				chats: state.chats.map(renameChat),
				workerRelatedChats: state.workerRelatedChats.map(renameChat),
			};
		}
		case "MARK_AGENT_CHATS_READ": {
			const agentKey = String(action.agentKey || "").trim();
			if (!agentKey) {
				return state;
			}
			return {
				...state,
				chats: markAgentChatsRead(state.chats, agentKey),
				workerRelatedChats: markWorkerRowsRead(state.workerRelatedChats, agentKey),
				agents: upsertAgentUnreadCount(state.agents, agentKey, 0),
			};
		}
		case "SET_CHAT_FILTER":
			return { ...state, chatFilter: action.filter };
		case "SET_CONVERSATION_MODE":
			return { ...state, conversationMode: action.mode };
		case "SET_WORKER_SELECTION_KEY":
			return { ...state, workerSelectionKey: action.workerKey };
		case "SET_WORKER_ROWS": {
			const workerIndexByKey = new Map(
				action.rows.map((row) => [row.key, row]),
			);
			const workerSelectionKey = workerIndexByKey.has(
				state.workerSelectionKey,
			)
				? state.workerSelectionKey
				: "";
			return {
				...state,
				workerRows: action.rows,
				workerIndexByKey,
				workerSelectionKey,
			};
		}
		case "SET_WORKER_RELATED_CHATS":
			return { ...state, workerRelatedChats: action.chats };
		case "SET_WORKER_CHAT_PANEL_COLLAPSED":
			return { ...state, workerChatPanelCollapsed: action.collapsed };
		case "SET_PENDING_NEW_CHAT_AGENT_KEY":
			return { ...state, pendingNewChatAgentKey: action.agentKey };
		case "SET_WORKER_PRIORITY_KEY":
			return { ...state, workerPriorityKey: action.workerKey };
		case "SET_TEMPORARY_PINNED_AGENT_KEY":
			return {
				...state,
				temporaryPinnedAgentKey: String(action.agentKey || "").trim(),
			};
		case "SET_CHAT_AGENT_BY_ID":
			return {
				...state,
				chatAgentById: setMapValue(
					state.chatAgentById,
					action.chatId,
					action.agentKey,
				),
			};
		default:
			return null;
	}
}
